import { formatDate as FnsFormatDate } from "date-fns";

export function formatDate(
  date: Date | string,
  separator: string = ","
): string {
  if (typeof date === "string") {
    date = new Date(date);
  }
  // const options: Intl.DateTimeFormatOptions = {
  //   month: "2-digit",
  //   day: "2-digit",
  //   year: "numeric",
  //   hour: "2-digit",
  //   minute: "2-digit",
  //   hour12: true,
  // };

  // return new Intl.DateTimeFormat("en-US", options).format(date);

  return FnsFormatDate(date, `MM/dd/yyyy${separator} hh:mm a`);
}

export function formatYear(date: Date | string): string {
  if (typeof date === "string") {
    date = new Date(date);
  }
  return FnsFormatDate(date, "yyyy");
}

export function formatDateWithSlashes(date: Date | string): string {
  if (typeof date === "string") {
    date = new Date(date);
  }
  return FnsFormatDate(date, "MM/dd/yyyy");
}

export const formatDateUTC = (s: string): string =>
  new Intl.DateTimeFormat(undefined, {
    timeZone: "UTC",
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  }).format(new Date(s));

export function formatTime(date: Date | string, ampm: boolean = true): string {
  if (typeof date === "string") {
    date = new Date(date);
  }
  return FnsFormatDate(date, ampm ? "hh:mm a" : "HH:mm");
}

export function formatSize(bytes: number): string {
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];

  if (bytes === 0) return "0 Byte";

  const i = Math.floor(Math.log(bytes) / Math.log(1024));

  return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(2))} ${sizes[i]}`;
}

export function scrollToBottom() {
  window.scrollTo({
    top: document.body.scrollHeight,
    behavior: "smooth",
  });
}

export function scrollToElement(element: HTMLElement) {
  if (!element) return;
  const elementPosition = element.getBoundingClientRect().top + window.scrollY;
  const offset = -100; // Offset value

  window.scrollTo({
    top: elementPosition + offset,
    behavior: "smooth",
  });
}

export function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text);
}

export function formatNumberBase(num: number, returnFormatted: boolean = false): string {
   if (num == null) {
    return "N/A"; // or another fallback value
  }
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(0).replace(/\.00$/, "") + "b";
  } else if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(0).replace(/\.00$/, "") + "m";
  } else if (num >= 10_000) {
    return (num / 1_000).toFixed(0).replace(/\.00$/, "") + "k";
  } else if (num >= 1_000) {
    return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
  } else {
    return returnFormatted ? formatNumber(num) : num.toString();
  }
}

export function formatNumberWithSuffix(
  num: number,
  returnFormatted: boolean = false
): string {
  return formatNumberBase(num, returnFormatted);
}

export function formatNumberWithSuffixPrefix(
  num: number,
  prefix: string = "$",
  returnFormatted: boolean = false
): string {
  if (num === 0) {
    return prefix + "0"; // Ensure prefix is applied to zero
  }
  return prefix + formatNumberBase(num, returnFormatted);
}

export function formatNumber(num: number): string {
  let formatter = new Intl.NumberFormat("en-US", {
    style: "decimal",
    maximumFractionDigits: 2,
  });
  return formatter.format(num);
}

export function formatBoolean(value: boolean): string {
  return value ? "YES" : "NO";
}

export function formatTagsarray(tags: string[]): string[] {
  return tags.map((t) => t.replaceAll("_", " "));
}

export function formatTagsArrayFromEnum<T extends string>(
  tags: string[],
  literal: (tag: T) => string
): string[] {
  return tags.map((tag) => {
    try {
      return literal(tag as T);
    } catch {
      throw new Error(`Unknown tag: ${tag}`);
    }
  });
}

export function matchesUUID(value: string): boolean {
  const uuid4Regex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return value.match(uuid4Regex) !== null;
}

export function getCaseIdFromUrl(): string | null {
  const urlSplits = window.location.pathname.split("/");
  if (urlSplits.length > 1 && matchesUUID(urlSplits[2])) {
    return urlSplits[2];
  }
  return null;
}

export function generateRoute(
  caseId: string,
  path: string,
  revisionId: string | null = null
): string {
  if (path === "revisions") {
    return `/cases/${caseId}/revisions`;
  }
  return `/cases/${caseId}${
    revisionId ? `/revisions/${revisionId}` : ""
  }/${path.replace(/^\//, "")}`;
}

export function cutOffText(
  text: string,
  maxLength: number = 100,
  position: "end" | "middle" = "end",
  suffix: string = "...",
): string {
  if (text.length <= maxLength) {
    return text;
  }
  if (position === "middle") {
    const halfLength = Math.floor(maxLength / 2);
    return text.slice(0, halfLength) + suffix + text.slice(-halfLength);
  }
  return text.slice(0, maxLength - suffix.length) + suffix;
}
